import React, { forwardRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getConfig, isValidDate } from "../../components/formatAmount";
import moment from "moment";
import InputField from "./InputField";

const formatMap = {
  "YYYY-MM-DD": "yyyy-MM-dd",
  "DD-MM-YYYY": "dd-MM-yyyy",
  "MM-DD-YYYY": "MM-dd-yyyy",
  "MM/DD/YYYY": "MM/dd/yyyy",
  "DD/MM/YYYY": "dd/MM/yyyy",
  "YYYY/MM/DD": "yyyy/MM/dd",
  "YYYY.MM.DD": "yyyy.MM.dd",
  "DD.MM.YYYY": "dd.MM.yyyy",
  "MM.DD.YYYY": "MM.dd.yyyy",
  "DD MMMM YYYY": "dd MMMM yyyy", // 28 May 2025
  "MMMM DD, YYYY": "MMMM dd, yyyy", // May 28, 2025
  "YYYY MMMM DD": "yyyy MMMM dd", // 2025 May 28
  "DD MMM YYYY": "dd MMM yyyy", // 28 May 2025 (short month)
  "MMM DD, YYYY": "MMM dd, yyyy", // May 28, 2025 (short month)
  "DD/MM/YY": "dd/MM/yy",
  "MM/DD/YY": "MM/dd/yy",
  "YY-MM-DD": "yy-MM-dd",
  "DD-MM-YY": "dd-MM-yy",
};

// Accept typed dates (full or day/month only → current year), matching system + common formats
const parseTypedDate = (rawText, configDateFormat, yearOnly = false) => {
  if (!rawText || typeof rawText !== "string") return null;
  const text = rawText.trim();
  if (!text) return null;

  if (yearOnly) {
    const year = moment(text, ["YYYY", "YY"], true);
    return year.isValid() ? year.toDate() : null;
  }

  const fullFormats = [
    configDateFormat,
    "DD/MM/YYYY",
    "DD-MM-YYYY",
    "DD.MM.YYYY",
    "DD/MM/YY",
    "DD-MM-YY",
    "MM/DD/YYYY",
    "MM-DD-YYYY",
    "YYYY-MM-DD",
    "YYYY/MM/DD",
    "D/M/YYYY",
    "D-M-YYYY",
    "D/M/YY",
    "D-M-YY",
  ].filter(Boolean);

  const full = moment(text, fullFormats, true);
  if (full.isValid()) return full.toDate();

  // e.g. "12/10" or "12-10" → 12th October of current year
  const dayMonth = moment(text, ["DD/MM", "DD-MM", "D/M", "D-M", "DD.MM", "D.M"], true);
  if (dayMonth.isValid()) {
    dayMonth.year(moment().year());
    return dayMonth.toDate();
  }

  return null;
};

const CustomInput = forwardRef(
  (
    {
      value,
      onClick,
      onChange,
      onFocus,
      onBlur,
      onKeyDown,
      placeholder,
      className,
      shouldShowError,
      error,
      disabled,
      ...rest
    },
    ref
  ) => (
    <InputField
      value={value}
      onClick={onClick}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      ref={ref}
      disabled={disabled}
      className={`text-base w-full ${
        error && shouldShowError ? "error" : ""
      } ${className}`}
      shouldShowError={shouldShowError}
      error={error}
      showErrorText={false} // Error text is handled by the parent
      {...rest}
    />
  )
);

CustomInput.displayName = "CustomInput";

const CustomDatePicker = ({
  value,
  onChange,
  width = "100%",
  style = {},
  bgColor,
  error,
  placeholder,
  shouldShowError,
  showErrorText = true,
  required = true,
  theme = "default",
  disabled = false,
  yearOnly = false,
  className = "",
}) => {
  const configDateFormat = getConfig().date_format;
  const format = yearOnly
    ? "yyyy"
    : formatMap[configDateFormat] || "yyyy-MM-dd";

  const applyTypedDate = (rawText) => {
    const parsed = parseTypedDate(rawText, configDateFormat, yearOnly);
    if (parsed) {
      onChange(parsed);
      return true;
    }
    return false;
  };

  return (
    <>
      <div style={{ width, ...style }} className="relative w-full">
        <DatePicker
          selected={value && isValidDate(value) ? new Date(value) : null}
          onChange={(date) => {
            onChange(date || null);
          }}
          onBlur={(e) => {
            applyTypedDate(e?.target?.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && applyTypedDate(e.target?.value)) {
              e.preventDefault();
              e.target?.blur?.();
            }
          }}
          showYearDropdown={!yearOnly}
          scrollableYearDropdown={!yearOnly}
          showMonthDropdown={!yearOnly}
          dropdownMode="select"
          dateFormat={format}
          required={required}
          disabled={disabled}
          placeholderText={placeholder || configDateFormat || "Select Date"}
          wrapperClassName="custom-datepicker-wrapper"
          isClearable={!disabled}
          popperPlacement="auto"
          popperModifiers={[
            {
              name: "offset",
              options: {
                offset: [0, 8],
              },
            },
            {
              name: "flip",
              options: {
                fallbackPlacements: ["top"],
                padding: 8,
              },
            },
            {
              name: "preventOverflow",
              options: {
                boundary: "viewport",
                padding: 8,
              },
            },
          ]}
          popperProps={{
            strategy: "fixed",
          }}
          popperClassName="datepicker-on-top"
          portalId="datepicker-portal"
          {...(yearOnly && {
            showYearPicker: true,
          })}
          customInput={
            <CustomInput
              placeholder={placeholder || configDateFormat || "Select Date"}
              error={error}
              shouldShowError={shouldShowError}
              className={`custom-datepicker ${className}`}
            />
          }
        />

        <style>
          {`
          .custom-datepicker-wrapper {
            width: ${width} !important;
            border-radius: 12px !important;
            position: relative !important;
          }
          .custom-datepicker {
            width: 100% !important;
            border-radius: 12px !important;
            background: ${
              theme !== "light" ? "var(--input-background-gray)" : "transparent"
            } !important;
          }
          .react-datepicker__input-container input {
            border-radius: 12px !important;
            background: ${bgColor};
            width: 100% !important;
            padding: 8px 16px !important;
            color: inherit;
          }
          /* Make Contract Date input text black wherever contract-date-field class is used */
          .react-datepicker__input-container input.contract-date-field {
            color: #000000 !important;
            -webkit-text-fill-color: #000000 !important;
            opacity: 1 !important;
          }
          /* Make Supplier Contract Date input text black wherever supplier-contract-date-field class is used */
          .react-datepicker__input-container input.supplier-contract-date-field {
            color: #000000 !important;
            -webkit-text-fill-color: #000000 !important;
            opacity: 1 !important;
          }
          .react-datepicker__input-container input:focus {
            outline: none !important;
            border-width: 2px !important;
            border-color: var(--p-green-light) !important;
            outline: none !important;
            box-shadow: none !important;
          }
          .react-datepicker__input-container input.error {
            border-width: 2px !important;
            border-color: var(--p-red) !important;
            outline: none !important;
            box-shadow: none !important;
          }
          .react-datepicker__year-select,
          .react-datepicker__month-select {
            background: white;
            padding: 8px 12px;
            border-right: 8px solid white !important;
          }
          .react-datepicker__triangle {
            display: none;
          }
          .react-datepicker__header__dropdown {
            margin: 12px !important;
            display: flex !important;
          }
          .datepicker-on-top {
            z-index: 99999 !important;
          }
          .react-datepicker {
            border-radius: 12px !important;
            padding: 12px 0px;
            background-color: #f9fafb !important;
            font-size: 12px !important;
            margin-bottom: 10px;
          }
          .react-datepicker__navigation--next,
          .react-datepicker__navigation--previous {
            top: 19px !important;
          }
          .react-datepicker__header {
            background-color: #f9fafb !important;
            border-bottom-width: 1px !important;
            border-bottom-color: var(--p-gray-dark) !important;
            border-radius: 12px 12px 0 0 !important;
          }
          .react-datepicker__current-month,
          .react-datepicker-time__header {
            font-weight: 600;
            color: #374151;
          }
          .react-datepicker__day-name,
          .react-datepicker__day,
          .react-datepicker__time-name {
            color: #1f2937 !important;
          }
          .react-datepicker__day--selected,
          .react-datepicker__day--keyboard-selected {
            background-color: var(--p-green-light) !important;
            color: white !important;
            border-radius: 8px !important;
          }
          .react-datepicker__day--selected:hover,
          .react-datepicker__day--keyboard-selected:hover {
            background-color: var(--p-green-light) !important;
          }
          .react-datepicker__day:not(.react-datepicker__day--selected):hover {
            background-color: var(--p-green-light) !important;
            color: white !important;
            border-radius: 8px !important;
          }
          .react-datepicker__year-dropdown,
          .react-datepicker__month-dropdown {
            border-radius: 8px !important;
            background: white;
            padding: 5px;
          }
          .react-datepicker__year-option,
          .react-datepicker__month-option {
            padding: 5px 10px;
            border-radius: 4px;
          }
          .react-datepicker__year-option:hover,
          .react-datepicker__month-option:hover {
            background-color: #eff6ff !important;
          }
          .react-datepicker__close-icon {
            padding: 0 !important;
            right: 3px !important; 
            top: -2px !important;
            font-size: 20px !important;
          }
          .react-datepicker__close-icon::after {
            background-color: transparent !important;
            font-size: 20px !important;
            padding: 2px 5px !important;
            color: rgb(204, 204, 204) !important;
            font-weight: 600 !important;
          }
          .react-datepicker__close-icon:hover::after {
            background-color: transparent !important;
            color: var(--p-gray-dark2) !important;
          }
        `}
        </style>
      </div>

      {error && shouldShowError && showErrorText && (
        <p className="text-red-500 text-base p-2">{error}</p>
      )}
    </>
  );
};

export default CustomDatePicker;
